import { useState } from "react"
import { Box, Stack, Typography } from "@mui/material"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faClockRotateLeft, faIdCard, faUserShield, faUserUnlock } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"

import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import { DrawerDetailRow } from "../../../../../../shared/components/drawers/DrawerDetailRow"
import { DrawerDetailsCard } from "../../../../../../shared/components/drawers/DrawerDetailsCard"
import { DrawerSection } from "../../../../../../shared/components/meetingServiceOrderDrawer/DrawerSection"
import { LocalizedEnum, LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useDateTime } from "../../../../../localization/useDateTime"

import { CompanyUserDetails, CompanyUserDetailsType } from "../companyUserDetailsTypes"

enum UserDetailsSection {
    ROLES = "roles",
    ID_CARD = "id-card",
    TRACKING = "tracking",
}

const ROLES_TRANSLATION_BASE: Record<CompanyUserDetailsType, string> = {
    [CompanyUserDetailsType.USER]: "company-user-details-role",
    [CompanyUserDetailsType.ADMIN]: "tenant-admin-role",
}

const useStyles = makeStyles()((theme) => ({
    scroll: {
        flex: 1,
        minHeight: 0,
        overflowY: "auto",
        padding: "1rem",
        background: theme.palette.tailwind.slate[100],
    },
    sectionRows: {
        padding: "0 1rem",
    },
    sectionText: {
        padding: "0 1rem 1rem",
        color: theme.palette.tailwind.slate[500],
    },
    chips: {
        padding: "0 1rem 1rem",
    },
    footer: {
        padding: "1rem",
        borderTop: `1px solid ${theme.palette.tailwind.slate[200]}`,
    },
    impersonateIcon: {
        fontSize: "1rem !important",
    },
}))

type CompanyUserDetailsBodyProps = Readonly<{
    user: CompanyUserDetails
    onEdit: () => void
    onImpersonate?: () => void
}>

export function CompanyUserDetailsBody({ user, onEdit, onImpersonate }: CompanyUserDetailsBodyProps) {
    const { classes } = useStyles()
    const dateTimeFactory = useDateTime()

    const [openSections, setOpenSections] = useState<Set<UserDetailsSection>>(
        new Set(Object.values(UserDetailsSection)),
    )
    const toggleSection = (key: UserDetailsSection) =>
        setOpenSections((prev) => {
            const next = new Set(prev)
            if (next.has(key)) next.delete(key)
            else next.add(key)
            return next
        })

    const { syncDetails, logins, roles } = user
    const isUser = user.type === CompanyUserDetailsType.USER
    const dash = "-"

    return (
        <>
            <Box className={classes.scroll}>
                <DrawerDetailsCard>
                    <DrawerDetailRow
                        label={
                            <LocalizedStrict id="company-user-details-account-user-id">
                                <span>User ID</span>
                            </LocalizedStrict>
                        }
                        value={user.id}
                    />
                    <DrawerDetailRow
                        label={
                            <LocalizedStrict id="company-user-details-account-username">
                                <span>Username</span>
                            </LocalizedStrict>
                        }
                        value={user.username || dash}
                    />
                    {isUser && (
                        <>
                            <DrawerDetailRow
                                label={
                                    <LocalizedStrict id="company-user-details-account-employee-id">
                                        <span>Employee ID</span>
                                    </LocalizedStrict>
                                }
                                value={user.employeeId || dash}
                            />
                            <DrawerDetailRow
                                label={
                                    <LocalizedStrict id="company-user-details-account-user-group">
                                        <span>User group</span>
                                    </LocalizedStrict>
                                }
                                value={user.userGroupName ?? dash}
                            />
                            <DrawerDetailRow
                                label={
                                    <LocalizedStrict id="company-user-details-account-department">
                                        <span>Department</span>
                                    </LocalizedStrict>
                                }
                                value={user.departmentName ?? dash}
                            />
                        </>
                    )}
                    <DrawerDetailRow
                        label={
                            <LocalizedStrict id="company-user-details-account-phone">
                                <span>Phone</span>
                            </LocalizedStrict>
                        }
                        value={user.phone || dash}
                    />
                    <DrawerDetailRow
                        label={
                            <LocalizedStrict id="company-user-details-account-language">
                                <span>Language</span>
                            </LocalizedStrict>
                        }
                        value={user.language || dash}
                    />
                </DrawerDetailsCard>

                <DrawerSection
                    icon={faUserShield}
                    titleId="company-user-details-section-roles"
                    titleFallback="Roles"
                    open={openSections.has(UserDetailsSection.ROLES)}
                    onToggle={() => toggleSection(UserDetailsSection.ROLES)}
                >
                    {roles.length > 0 ? (
                        <Stack direction="row" flexWrap="wrap" gap=".5rem" className={classes.chips}>
                            {roles.map((role) => (
                                <LocalizedEnum key={role} base={ROLES_TRANSLATION_BASE[user.type]} enumValue={role}>
                                    <StandardChip variant="grey">{role}</StandardChip>
                                </LocalizedEnum>
                            ))}
                        </Stack>
                    ) : (
                        <LocalizedStrict id="company-user-details-roles-empty">
                            <Typography variant="body2" className={classes.sectionText}>
                                No roles assigned.
                            </Typography>
                        </LocalizedStrict>
                    )}
                </DrawerSection>

                {isUser && (
                    <>
                        <DrawerSection
                            icon={faIdCard}
                            titleId="company-user-details-section-idcard"
                            titleFallback="ID card"
                            open={openSections.has(UserDetailsSection.ID_CARD)}
                            onToggle={() => toggleSection(UserDetailsSection.ID_CARD)}
                        >
                            <LocalizedStrict id="company-user-details-idcard-empty">
                                <Typography variant="body2" className={classes.sectionText}>
                                    No ID card connected.
                                </Typography>
                            </LocalizedStrict>
                        </DrawerSection>

                        <DrawerSection
                            icon={faClockRotateLeft}
                            titleId="company-user-details-section-tracking"
                            titleFallback="Tracking"
                            open={openSections.has(UserDetailsSection.TRACKING)}
                            onToggle={() => toggleSection(UserDetailsSection.TRACKING)}
                        >
                            <Box className={classes.sectionRows}>
                                <DrawerDetailRow
                                    label={
                                        <LocalizedStrict id="company-user-details-last-signin">
                                            <span>Last sign-in</span>
                                        </LocalizedStrict>
                                    }
                                    value={
                                        logins?.lastLogin ? (
                                            dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(logins.lastLogin))
                                        ) : (
                                            <LocalizedStrict id="company-user-details-never">
                                                <span>Never</span>
                                            </LocalizedStrict>
                                        )
                                    }
                                />
                                {syncDetails ? (
                                    <>
                                        <DrawerDetailRow
                                            label={
                                                <LocalizedStrict id="company-user-details-sync-source">
                                                    <span>Sync source</span>
                                                </LocalizedStrict>
                                            }
                                            value={syncDetails.source}
                                        />
                                        <DrawerDetailRow
                                            label={
                                                <LocalizedStrict id="company-user-details-sync-last-synced">
                                                    <span>Last synced</span>
                                                </LocalizedStrict>
                                            }
                                            value={dateTimeFactory.formatDateTime(
                                                dateTimeFactory.fromApi(syncDetails.lastSynced),
                                            )}
                                        />
                                    </>
                                ) : (
                                    <DrawerDetailRow
                                        label={
                                            <LocalizedStrict id="company-user-details-sync-status">
                                                <span>User sync</span>
                                            </LocalizedStrict>
                                        }
                                        value={
                                            <LocalizedStrict id="company-user-details-sync-none">
                                                <span>Not synchronized</span>
                                            </LocalizedStrict>
                                        }
                                    />
                                )}
                            </Box>
                        </DrawerSection>
                    </>
                )}
            </Box>

            <Stack direction="row" spacing="1rem" className={classes.footer}>
                {onImpersonate && (
                    <Box flex={1}>
                        <LocalizedStrict id="company-user-details-impersonate-button">
                            <StandardButton
                                variant="outlined"
                                size="medium"
                                startIcon={<FontAwesomeIcon icon={faUserUnlock} className={classes.impersonateIcon} />}
                                onClick={onImpersonate}
                            >
                                Log in as
                            </StandardButton>
                        </LocalizedStrict>
                    </Box>
                )}
                <Box flex={1}>
                    <LocalizedStrict id="company-user-details-edit-button">
                        <StandardButton size="medium" onClick={onEdit}>
                            Edit user
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </Stack>
        </>
    )
}
